import type { ReactNode } from "react";
import type { SubjectDto } from "@mmm/contracts";
import { DashboardPanel } from "../molecules/DashboardPanel.js";
import { LoadingSkeleton } from "../ui/LoadingPlaceholders.js";
import { ViewModeToggle, type ViewMode } from "../ui/ViewModeToggle.js";
import { usePersistedViewMode } from "../ui/usePersistedViewMode.js";
import { SubjectCard } from "./SubjectCard.js";
import { useSubjectDialog } from "./SubjectDialogProvider.js";
import { Card, EmptyState, ErrorState, Grid, Icon, useT } from "@mmm/ui";
interface SubjectPanelProps {
  subjects: SubjectDto[];
  loading: boolean;
  /** Set once the subjects query has exhausted retries — renders an
   * ErrorState with Retry instead of the grid. Leave unset (or pass
   * `false`) while a retry is still in flight. */
  hasError?: boolean;
  onRetry?: () => void;
  title: string;
  subtitle?: string;
  toolbar?: ReactNode;
  emptyTitle: string;
  emptyMessage?: string;
  emptyAction?: ReactNode;
  className?: string;
  addNavigateOnCreate?: boolean;
  viewStorageKey?: string;
  defaultViewMode?: ViewMode;
}

export function SubjectPanel({
  subjects,
  loading,
  hasError = false,
  onRetry,
  title,
  subtitle,
  toolbar,
  emptyTitle,
  emptyMessage,
  emptyAction,
  className = "",
  addNavigateOnCreate = false,
  viewStorageKey = "subjects",
  defaultViewMode = "grid",
}: SubjectPanelProps) {
  const { openCreate } = useSubjectDialog();
  const t = useT();
  const [viewMode, setViewMode] = usePersistedViewMode(viewStorageKey, defaultViewMode);

  return (
    <DashboardPanel
      title={title}
      subtitle={subtitle}
      className={`mmm-subject-panel ${className}`.trim()}
      toolbar={
        <div className="mmm-subject-panel-toolbar">
          {toolbar}
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </div>
      }
    >
      {hasError ? (
        <ErrorState variant="panel" title={t("subjects.loadFailed")} onRetry={onRetry} />
      ) : (
        <Grid
          variant={viewMode}
          minItemWidth={210}
          gap={viewMode === "grid" ? "lg" : "sm"}
          className={`mmm-subject-card-grid mmm-subject-card-grid--${viewMode}`}
        >
          <button
            type="button"
            className="mmm-subject-add-card"
            onClick={() => openCreate({ navigateOnCreate: addNavigateOnCreate })}
          >
            <span className="mmm-subject-add-card-icon">
              <Icon name="add" />
            </span>
            <span className="mmm-subject-add-card-title">{t("subjects.addSubject")}</span>
          </button>

          {loading ? (
            <SubjectCardSkeletons />
          ) : (
            subjects.map((subject) => <SubjectCard key={subject.id} subject={subject} />)
          )}

          {!loading && subjects.length === 0 && (
            <EmptyState variant="panel" title={emptyTitle} message={emptyMessage} action={emptyAction} />
          )}
        </Grid>
      )}
    </DashboardPanel>
  );
}

function SubjectCardSkeletons() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <Card className="mmm-loading-card mmm-subject-card" key={index}>
          <LoadingSkeleton height={26} width="26px" className="mmm-skeleton--swatch" />
          <LoadingSkeleton height={17} width={index % 2 === 0 ? "68%" : "54%"} />
          <LoadingSkeleton height={24} width="82px" />
        </Card>
      ))}
    </>
  );
}
